import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import isEqual from 'react-fast-compare';

import { selectUIComponent, formAddOnUIComponent } from 'ctm-css-themes';
import { get, rulesCache, applyVerticalStyles, pageContext, interpolateString } from 'ctm-util-functions';

import toolTipStyles from '../../tooltip/tooltip.css';
import { getDisabledProp, getComponentStateSuffix, getRecordingMaskClass } from '../utils';

import * as actions from '../actions';

/**
 _Select component for <select> elements
 */
class _Select extends Component {

    constructor(props) {
        super(props);
        this.styles = applyVerticalStyles(selectUIComponent, 'select');
        this.formAddOnUIComponent = applyVerticalStyles(formAddOnUIComponent, 'add_on');
    }

    /**
     * Update the touch for this select if the actionType matches the validation trigger
     * @param {String} actionType - The type of action that initiated the value change
     */
    elementTouched = (actionType) => {
        if (!get(this.props, 'rules.validation')) {
            return;
        }
        const { rules, dispatch, schema } = this.props;
        const { validation } = rules;
        const elementRule = rulesCache.getElementRuleForId(validation.id);

        if (elementRule.props.trigger === actionType) {
            dispatch(
                actions.elementTouched(
                    schema.touchIdentifier || schema.identifier,
                    schema.stateKey
                )
            );
        }
    };

    /**
     * Set the value to state for this input.
     * If value equals the defaultOption for this input, don't set the value on state.
     * @param {String} inputValue The value to set for this input.
     */
    setValue = (inputValue) => {
        const {
            schema,
            value,
            ignoreSetValue,
            dispatch
        } = this.props;
        const optionsConfig = schema.field.options;
        if (inputValue === optionsConfig.defaultOption || inputValue === value || ignoreSetValue) {
            return;
        }

        // update controlled value
        dispatch(
            actions.setValue(
                schema.backendIdentifier || schema.identifier,
                // Don't set an empty string on state. We can't use null as a value as that makes the element uncontrolled
                // and results in lots of warnings and side-effects when an actual value is then set
                inputValue || null,
                schema.stateKey
            )
        );
        this.elementTouched('onChange');
    };

    /**
     * onBlur event handler
     * @param {Object} event - The event triggered by blur
     */
    onBlur = (event) => {
        const { onBlur } = this.props;
        this.elementTouched('onBlur');
        // Callback if onBlur prop is passed.
        const eventBlur = onBlur;
        if (eventBlur && typeof eventBlur === 'function') {
            eventBlur(event);
        }
    };

    /**
     * Set the first value in this.props.options to the state
     */
    setFirstValue = () => {
        const { options, isDefault } = this.props;
        if (options.length > 0 && !isDefault) {
            this.setValue(options[0].value);
        }
    };

    /**
     * If there are options defined for the select when it updates, set the first
     * option as the value for the input.
     * @param {Object} prevProps - The component's props previous to this update
     */
    componentDidUpdate = (prevProps) => {
        const { options } = this.props;
        if (!isEqual(prevProps.options, options)) {
            this.setInitialValue();
        }
    };

    /**
     * Helper method for setting state of Select during mounting or updating
     */
    setInitialValue = () => {
        const { value } = this.props;
        if (this.hasOption(value)) {
            this.setValue(value);
        } else {
            this.setFirstValue();
        }
    };

    /**
     * Return true if the given value is one of the options in the select, false otherwise
     * @param {String} value - The current selected value
     * @returns {Boolean} - True if there is an option for value, false otherwise.
     */
    hasOption = (value) => {
        const { options } = this.props;
        for (const opt of options) {
            if (opt.value === value) {
                return true;
            }
        }
        return false;
    };

    /**
     * Default onChange function for the Select component
     * @param {Object} event - The event triggered by the change
     */
    onChange = (event) => {
        const { onChange } = this.props;
        // Default Form Actions
        this.setValue(event.target.value);
        // Callback if onChange prop is passed.
        const eventChange = onChange;
        if (eventChange && typeof eventChange === 'function') {
            eventChange(event);
        }
    };

    /**
     * Construct the options
     * @param {Object} options The options object
     * @param {String} elementId The identifier
     * @returns {Array} An array of options.
     */
    buildOptions = (options, elementId) => options.map(({ value, label }) => (
        <option value={value} key={`${elementId}.${value}`}>
            {label}
        </option>
    ));

    /**
     * Renders a select element with options based on the supplied props
     * @returns {Node} The complete select element
     */
    render = () => {
        const { schema, options, value, selectedValues } = this.props;
        const elementId = schema.identifier;
        const menuOptions = this.buildOptions(options, elementId);
        const disabledProps = getDisabledProp(this.props);

        const UIComponentPrefix = schema.field.UIComponent || 'select_medium';
        const stateSuffix = getComponentStateSuffix(this.props);
        const UIComponent = `${UIComponentPrefix}${stateSuffix}`;
        const componentClasses = classNames(this.styles[UIComponent], getRecordingMaskClass(schema));

        // select menu addon styles
        const addonWidthClass = schema.field.addonWidth || 'default';
        const addonClasses = classNames(
            this.formAddOnUIComponent[`addon${stateSuffix}`],
            this.formAddOnUIComponent.addon_suffix,
            this.formAddOnUIComponent[`addon_width_${addonWidthClass}`],
            this.formAddOnUIComponent.addon_suffix_menu);

        const notificationClass = classNames(
            toolTipStyles.tooltip_inner_base,
            toolTipStyles.variant_grey__tooltip_inner,
        );

        // eslint-disable-next-line no-shadow, react/destructuring-assignment
        const current = options.find(({ value }) => value === this.props.value);

        return (
            <>
                <label htmlFor={elementId} className={addonClasses}>
                    <select
                        key={elementId}
                        name={elementId}
                        data-id={elementId}
                        id={elementId}
                        onChange={this.onChange}
                        className={componentClasses}
                        value={value}
                        onBlur={ this.onBlur }
                        {...disabledProps}
                    >
                        {menuOptions}
                    </select>
                </label>
                {current && current.notificationText && (
                    <div
                        className={notificationClass}
                        style={{ marginTop: '1.2rem' }}
                    >
                        {interpolateString(current.notificationText.content)(selectedValues)}
                    </div>
                )}
            </>
        );
    };

}

_Select.propTypes = {
    schema: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    selectedValues: PropTypes.object,
    /** Mapped in state to props * */
    options: PropTypes.array.isRequired,
    isDefault: PropTypes.bool.isRequired,

    rules: PropTypes.object,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    // used for SelectFilter where we handle setting the value via sandboxed results values
    ignoreSetValue: PropTypes.bool
};

/**
 * Get the array of {label, value} option objects for a select.
 * Look up the stateKey for the select and make the options.
 * @param  {Object} state         The app state
 * @param  {Object} optionsConfig The config for the options for this select - from the schema
 * @returns {Array}                The array of options - empty if state[stateKey] is empty
 */
const getOptionsFromState = (state, optionsConfig) => {
    const options = [];
    if (optionsConfig.initialOption) {
        options.push({
            label: optionsConfig.initialOption,
            value: optionsConfig.initialOption
        });
    }
    // get the set of objects used to make the options from state
    const stateOptions = get(state, optionsConfig.stateKey);
    for (const opt of stateOptions) {
        options.push({
            label: get(opt, optionsConfig.labelKey),
            value: get(opt, optionsConfig.valueKey)
        });
    }
    return options;
};

/**
 * Return the default option for the select
 * @param  {Object} optionsConfig The config for the options for this select - from the schema
 * @returns {Array}                The array with the single default option.
 */
const getDefaultOptions = (optionsConfig) => {
    return [{
        label: optionsConfig.defaultOption || '',
        value: optionsConfig.defaultOption || ''
    }];
};

/**
 * Map the required state for the select onto it's props.
 * Perform the usual mapStateToProps from formElementMapStateToProps, but also add the options
 * for the select
 * @param  {Object} state The app state
 * @param  {Object} props The component props
 * @returns {Object}       The props for the component, mapped from state.
 */
const mapStateToProps = (state, props) => {
    const optionsConfig = props.schema.field.options;
    let options = props.options;
    let isDefault = false;
    if (optionsConfig.stateKey) {
        const additionalOptions = props.schema.field.options.additionalOptions;
        if (optionsConfig.stateKey.startsWith('readOnly')) {
            const readOnlyKey = optionsConfig.stateKey.split(':')[1];
            options = pageContext.getProps().verticalReadOnlyState[readOnlyKey];
        } else {
            options = getOptionsFromState(state, optionsConfig);
        }
        // no options available from state yet, so use the defaults
        if (options.length === 0) {
            options = getDefaultOptions(optionsConfig);
            isDefault = true;
        } else if (options.length > 0 && additionalOptions) {
            options = options.concat(additionalOptions);
        }
    } else {
        options = props.schema.field.options;
    }

    // @todo: need a better namespace for options
    return { options, isDefault };
};

export { _Select };
export const Select = connect(mapStateToProps)(_Select);
